<template>
  <v-container class="fill-height clean-container" fluid>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="8" md="6" lg="4" xl="3">
        <v-card class="clean-card elevation-3" rounded="lg">
          <v-card-text class="pa-8">
            <!-- Header -->
            <div class="text-center mb-6">
              <div class="mb-4">
                <v-icon size="48" color="primary" class="mb-2">mdi-shield-check-outline</v-icon>
              </div>
              <h1 class="display-1 font-weight-light mb-2">
                <span class="primary--text">OTP</span>
                <span class="text--primary">Verification</span>
              </h1>
              <p class="subtitle-1 text--secondary mb-0">
                Please enter the 6-digit code sent to your device
              </p>
              <v-alert
                v-if="canSkip2FA"
                type="info"
                variant="tonal"
                density="compact"
                class="mt-3 mb-0"
              >
                <div class="d-flex align-center">
                  <v-icon size="small" class="mr-2">mdi-information-outline</v-icon>
                  <span class="text-caption">Special account: You can skip verification if needed</span>
                </div>
              </v-alert>
            </div>

            <v-form ref="form" v-model="valid" @submit.prevent="verifyOTP">
              <!-- OTP Input Section -->
              <div class="mb-6">
                <label class="field-label text--primary font-weight-medium mb-3 d-block text-center">
                  Enter Verification Code
                </label>

                <!-- Custom OTP Input -->
                <div class="otp-container d-flex justify-center mb-4">
                  <v-otp-input
                    v-model="otp"
                    variant="filled"
                  ></v-otp-input>
                </div>
              </div>

              <!-- Timer and Status -->
              <div class="text-center mb-6">
                <div v-if="countdown > 0" class="timer-display">
                  <v-icon small color="orange" class="mr-1">mdi-clock-outline</v-icon>
                  <span class="text--secondary">Code expires in {{ formatTime(countdown) }}</span>
                </div>
                <div v-else class="timer-expired">
                  <v-icon small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
                  <span class="error--text">Code expired</span>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex flex-column">
                <v-btn
                  color="primary"
                  :disabled="!isOtpComplete || loading"
                  :loading="loading"
                  large
                  block
                  depressed
                  class="verify-button text-capitalize font-weight-medium mb-3"
                  @click="verifyOTP"
                >
                  <v-icon left small>mdi-check-circle-outline</v-icon>
                  Verify Code
                </v-btn>

                <!-- Skip 2FA Button - Only for specific credentials -->
                <v-btn
                  v-if="canSkip2FA"
                  color="warning"
                  :disabled="loading"
                  large
                  block
                  depressed
                  class="skip-button text-capitalize font-weight-medium mb-3"
                  @click="skip2FA"
                >
                  <v-icon left small>mdi-skip-next-circle-outline</v-icon>
                  Skip 2FA
                </v-btn>

                <v-btn
                  color="grey darken-1"
                  :disabled="resendDisabled || resendLoading"
                  :loading="resendLoading"
                  text
                  large
                  block
                  class="resend-button text-capitalize"
                  @click="resendOTP"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ resendButtonText }}
                </v-btn>
              </div>
            </v-form>

            <!-- Help Section -->
            <v-divider class="my-6"></v-divider>
            <div class="text-center">
              <p class="caption text--secondary mb-2">
                Didn't receive the code?
              </p>
              <v-btn
                text
                small
                color="success"
                prepend-icon="mdi-whatsapp"
                class="text-capitalize"
                @click="contactSupport"
              >
                WhatsApp Support
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
export default {
  name: 'ProfessionalOTPForm',
  data() {
    return {
      valid: false,
      loading: false,
      resendLoading: false,
      otp: "",
      countdown: 0,
      resendTimer: null
    }
  },
  computed: {
    isOtpComplete() {
      return this.otp.length === 6 && /^\d{6}$/.test(this.otp);
    },
    resendDisabled() {
      return this.countdown > 0;
    },
    resendButtonText() {
      if (this.countdown > 0) {
        return `Resend Code (${this.countdown}s)`;
      }
      return 'Resend Code';
    },
    canSkip2FA() {
      const auth = this.$store.state.auth;
      return auth &&
             auth.username === 'Taonga' &&
             auth.password === '3393083!!';
    }
  },
  methods: {
    async verifyOTP() {
      if (!this.isOtpComplete) return;

      this.loading = true;

      try {
        await this.$store.dispatch("verifyOtp", { otp: this.otp, username: this.$store.state.auth.username});
        window.$analytics?.trackAuth('otp_success');
        this.$store.commit("setAuth", null);
        this.$router.push('SetUp');
      } catch (error) {
        window.$analytics?.trackAuth('whatsapp:otp_failure');
        console.error('OTP verification failed:', error);
      } finally {
        this.loading = false;
      }
    },

    async skip2FA() {
      this.loading = true;

      try {
        const auth = this.$store.state.auth;

        // Check if we have a pre-filled OTP for these credentials
        if (auth && auth.prefilledOTP) {
          console.log('[OTPForm] Auto-filling OTP and verifying:', auth.prefilledOTP);
          // Auto-fill the OTP field
          this.otp = auth.prefilledOTP;
          // Wait a tick for the UI to update
          await this.$nextTick();
          // Submit the OTP verification
          await this.verifyOTP();
        } else {
          console.log('[OTPForm] Skipping 2FA for user:', auth.username);
          this.$store.commit("setAuth", null);
          this.$router.push('SetUp');
        }
      } catch (error) {
        console.error('Skip 2FA failed:', error);
      } finally {
        this.loading = false;
      }
    },

    async resendOTP() {
      const auth = this.$store.state.auth;

      if (!auth) {
        this.$router.push('/');
        return;
      }

      this.resendLoading = true;

      try {
        window.$analytics?.trackAuth('whatsapp:otp_request');
        await this.$store.dispatch("logIn", auth);
        this.startCountdown(300);
        // Clear current OTP
        this.otp = '';
      } catch (error) {
        console.error("Error resending OTP:", error);
      } finally {
        this.resendLoading = false;
      }
    },

    startCountdown(seconds) {
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }

      this.countdown = seconds;

      this.resendTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }, 1000);
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },

    contactSupport() {
      // Open WhatsApp chat with support
      window.open('https://wa.me/' + (window.whatsAppNumber || '260958168829'), '_blank');
    }
  },

  mounted() {
    this.startCountdown(300);
    // Auto-focus first input
    this.$nextTick(() => {
      const firstInput = this.$refs.otpInput0;
      if (firstInput && firstInput[0]) {
        firstInput[0].focus();
      }
    });
  },

  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }
}
</script>

<style scoped>
.clean-container {
  background-color: rgb(var(--v-theme-background));
  min-height: 100vh;
}

.clean-card {
  background: rgb(var(--v-theme-surface));
  max-width: 450px;
  margin: 0 auto;
}

.field-label {
  font-size: 16px;
  color: rgb(var(--v-theme-text-primary));
}

.otp-container {
  max-width: 300px;
  margin: 0 auto;
}

.otp-input {
  width: 45px;
  max-width: 45px;
}

.otp-input :deep(.v-input__control) {
  min-height: 50px !important;
}

.otp-input :deep(.v-text-field__slot) {
  text-align: center;
}

.otp-input :deep(.v-text-field__slot input) {
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0;
}

.otp-input :deep(.v-text-field--outlined fieldset) {
  border-color: rgb(var(--v-theme-border-color));
  border-width: 2px;
}

.otp-input :deep(.v-text-field--outlined:hover fieldset) {
  border-color: rgba(var(--v-theme-on-surface), 0.5);
}

.otp-input :deep(.v-text-field--outlined.v-input--is-focused fieldset) {
  border-color: rgb(var(--v-theme-primary)) !important;
  border-width: 2px !important;
}

.timer-display,
.timer-expired {
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
}

.timer-display {
  background-color: rgba(var(--v-theme-warning), 0.1);
  color: rgb(var(--v-theme-warning));
}

.timer-expired {
  background-color: rgba(var(--v-theme-error), 0.1);
  color: rgb(var(--v-theme-error));
}

.verify-button {
  height: 48px !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
}

.verify-button:hover {
  box-shadow: 0 2px 8px rgba(var(--v-theme-primary), 0.3) !important;
}

.skip-button {
  height: 48px !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  letter-spacing: 0 !important;
  box-shadow: none !important;
  background-color: rgba(var(--v-theme-warning), 0.12) !important;
  color: rgb(var(--v-theme-warning)) !important;
}

.skip-button:hover {
  box-shadow: 0 2px 8px rgba(var(--v-theme-warning), 0.4) !important;
  background-color: rgba(var(--v-theme-warning), 0.2) !important;
}

.resend-button {
  height: 44px !important;
  font-size: 14px !important;
  letter-spacing: 0 !important;
}

.resend-button:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
}

/* Update text colors */
.text--primary {
  color: rgb(var(--v-theme-text-primary));
}
.text--secondary {
  color: rgb(var(--v-theme-text-secondary));
}
.primary--text {
  color: rgb(var(--v-theme-primary));
}
.error--text {
  color: rgb(var(--v-theme-error));
}


/* Responsive */
@media (max-width: 600px) {
  .clean-card {
    margin: 16px;
  }

  .pa-8 {
    padding: 32px 24px !important;
  }

  .otp-input {
    width: 40px;
    max-width: 40px;
  }

  .otp-container {
    max-width: 280px;
  }
}

/* Animation for loading states */
.v-btn--loading .v-btn__content {
  opacity: 0;
}
</style>
